<template>
  <!-- Demo Mode Badge -->
  <div
    v-if="isDemo"
    class="flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 whitespace-nowrap dark:bg-amber-500/20"
    :class="{ 'h-8! py-0!': props.size === 'small' }">
    <div class="flex items-center gap-1.5">
      <span class="text-sm font-medium text-brand-text dark:text-white">DEMO</span>
    </div>
    <a
      href="/"
      class="cursor-pointer text-xs font-bold text-brand-text underline opacity-80 dark:text-white"
      title="Exit demo mode">
      Exit
    </a>
  </div>

  <!-- Regular Team Selector -->
  <Select
    ref="selectRef"
    v-else
    class="min-w-0"
    :class="
      props.variant === 'shell'
        ? 'h-[30px] rounded-md! border-0! bg-transparent! shadow-none! hover:bg-tint! [&_.p-select-dropdown]:w-6! [&_.p-select-label]:flex! [&_.p-select-label]:items-center! [&_.p-select-label]:py-0! [&_.p-select-label]:pr-0! [&_.p-select-label]:pl-1!'
        : ['w-full flex-1', { 'h-8!': props.size === 'small' }]
    "
    :options="groupedOptions"
    option-value="pid"
    option-label="name"
    :option-group-label="showGroups ? 'label' : undefined"
    :option-group-children="showGroups ? 'items' : undefined"
    :size="props.size"
    :filter="teamOptions.length > 6"
    :filter-fields="['name', 'website']"
    filter-placeholder="Search projects"
    :placeholder="currentTeamLabel || 'Select a project'"
    @change="selectTeam">
    <template #value>
      <div class="flex min-w-0 items-center gap-2">
        <span
          v-if="props.variant === 'shell' && currentTeamLabel"
          class="flex size-5 shrink-0 items-center justify-center rounded-[5px] bg-[var(--rm-primary-soft)] text-[10px] font-bold text-primary"
          aria-hidden="true">
          {{ currentTeamLabel.charAt(0).toUpperCase() }}
        </span>
        <div
          class="min-w-0 flex-1 truncate"
          :class="[
            props.variant === 'shell' ? 'text-[13px] font-medium text-ink' : 'text-black dark:text-white/80',
            props.variant !== 'shell' && (props.size === 'small' ? 'text-[13px]' : 'text-sm')
          ]">
          <template v-if="!currentTeam">
            <span class="opacity-50">Select a project</span>
          </template>
          <template v-else-if="currentTeamLabel">
            {{ currentTeamLabel }}
          </template>
          <template v-else-if="doesOwnCurrentTeam">
            <span class="opacity-50">New project</span>
          </template>
          <template v-else>
            <span class="opacity-50">Select a project</span>
          </template>
        </div>
      </div>
    </template>
    <template v-if="props.variant === 'shell'" #dropdownicon>
      <ChevronUpDownIcon class="size-3.5 text-muted" aria-hidden="true" />
    </template>
    <template v-if="showGroups" #optiongroup="slotProps">
      <div class="flex min-w-0 items-center gap-2 text-xs font-semibold text-muted" data-test="project-group">
        <button
          v-if="slotProps.option.organizationPid"
          type="button"
          class="min-w-0 cursor-pointer truncate hover:text-primary hover:underline"
          :title="`Open ${slotProps.option.label}`"
          @click.stop="openOrganization(slotProps.option.organizationPid)">
          {{ slotProps.option.label }}
        </button>
        <span v-else class="min-w-0 truncate">{{ slotProps.option.label }}</span>
        <span v-if="slotProps.option.via.length" class="shrink-0 font-normal">
          via {{ slotProps.option.via.join(', ') }}
        </span>
      </div>
    </template>
    <template #option="slotProps">
      <div class="block text-sm">
        <div class="flex items-center space-x-1.5">
          <img
            v-if="slotProps.option.logoDomain"
            :src="`https://img.logo.dev/${slotProps.option.logoDomain}?token=pk_cZiWDpV1RAC_ml3vv_hAww&size=200`"
            class="h-6 w-6 rounded-full" />
          <span class="ml-2 text-base font-normal">
            {{ slotProps.option.name }}
          </span>
          <span v-if="slotProps.option.pid === currentTeam?.pid && currentRole" class="text-xs text-muted">
            {{ currentRole }}
          </span>
          <Tag v-if="slotProps.option.is_client_team" value="Client" severity="secondary" class="ml-1" />
          <Tag v-if="slotProps.option.is_demo" value="Demo" severity="secondary" class="ml-1" />
          <span class="opacity-30">/</span>
          <span class="opacity-50">
            {{ slotProps.option.website }}
          </span>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="border-t border-line p-1.5">
        <button
          v-if="currentTeam"
          type="button"
          class="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-heading hover:bg-overlay"
          @click="openProjectSettings">
          <Cog6ToothIcon class="size-4 text-muted" aria-hidden="true" />
          <span>Project settings</span>
        </button>
        <button
          type="button"
          class="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-heading hover:bg-overlay"
          @click="addNewProject">
          <PlusIcon class="size-4 text-muted" aria-hidden="true" />
          <span>New project</span>
        </button>
      </div>
    </template>
  </Select>

  <NewAccountDialog v-model="newAccountDialogVisible" after-create="onboarding" />
</template>

<script setup lang="ts">
import { Cog6ToothIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { shellIcons } from '@dashboard/components/icons/shellIcons'
import NewAccountDialog from '@dashboard/modules/onboarding/components/NewAccountDialog.vue'
import { domainNameForWebsite } from '@dashboard/modules/settings/services/domainUtilities'
import { ROUTES } from '@dashboard/router/routeNames'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { groupProjects } from '@dashboard/stores/projectGroups'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { storeToRefs } from 'pinia'
import Select from 'primevue/select'
import Tag from 'primevue/tag'
import { computed, ref, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'

const { chevronUpDown: ChevronUpDownIcon } = shellIcons

const props = defineProps<{
  size?: 'small' | 'large'
  selectedLabel?: string | null
  variant?: 'default' | 'shell'
}>()

const router = useRouter()

const teamStore = useTeamStore()
const { teams, doesOwnCurrentTeam, isDemo } = storeToRefs(teamStore)

const configStore = useConfigStore()
const { currentTeam, owner } = storeToRefs(configStore)
const { user } = storeToRefs(useAuthStore())
const selectRef = useTemplateRef<InstanceType<typeof Select>>('selectRef')

const currentRole = computed(() => {
  if (!owner.value || !user.value) {
    return null
  }
  return owner.value.id === user.value.id ? 'Owner' : 'Collaborator'
})
const teamOptions = computed(() =>
  teams.value.map((team) => ({
    ...team,
    logoDomain: domainNameForWebsite(team.website)
  }))
)

const memberOrganizationPids = computed(() => (user.value?.organizations ?? []).map((org) => org.pid))
const projectGroups = computed(() => groupProjects(teamOptions.value, memberOrganizationPids.value))
// One unlabeled group (demo, or a list before organizations exist) stays a flat list.
const showGroups = computed(() => projectGroups.value.some((group) => group.name !== null))
const groupedOptions = computed(() =>
  showGroups.value
    ? projectGroups.value.map((group) => ({
        label: group.name ?? '',
        organizationPid: group.memberOrganizationPid,
        via: group.via,
        items: group.projects
      }))
    : teamOptions.value
)

const openOrganization = (orgPid: string): void => {
  selectRef.value?.hide()
  void router.push({
    name: ROUTES.ACCOUNT_ORGANIZATION,
    params: { teamPid: currentTeam.value?.pid ?? teams.value[0]?.pid, orgPid }
  })
}

const readNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const currentTeamLabel = computed(
  () => readNonEmptyString(props.selectedLabel) ?? readNonEmptyString(currentTeam.value?.name)
)

const newAccountDialogVisible = ref(false)

const selectTeam = ({ value }: { value: number }): void => {
  // Force a full page reload to avoid state issues
  window.location.href = `/${value}/dashboard`
}

const openProjectSettings = (): void => {
  selectRef.value?.hide()
  void router.push({ name: ROUTES.SETTINGS, params: { teamPid: currentTeam.value?.pid } })
}

const addNewProject = (): void => {
  selectRef.value?.hide()
  newAccountDialogVisible.value = true
}
</script>
